import React from 'react';
import { Image } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL } from '@noths/polaris-client-utils';
import { IconButton } from '@noths/polaris-design-system-core-components';
import dynamic from 'next/dynamic';

import { CarouselDots } from 'src/components/atoms/CarouselDots/CarouselDots';
import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { Modal } from 'src/components/molecules/Modal/Modal';
import * as modalStyles from 'src/components/molecules/Modal/styles/ModalExternalStyles';
import { ModalLoader } from 'src/components/molecules/ModalLoader/ModalLoader';
import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation/PreventSwipeNavigation';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import { Thumbnails } from 'src/components/molecules/Thumbnails/Thumbnails';
import {
  MEDIA_ID_PREFIX,
  PRODUCT_MEDIA_TABPANEL_CONTAINER_ID,
  PRODUCT_MEDIA_TABPANEL_ID_PREFIX,
} from 'src/components/organisms/ProductMediaCarousel/constants/elementIds';
import { mediaKey } from 'src/components/organisms/ProductMediaCarousel/media/buildProductMedia';
import type {
  MediaItem,
  MediaLocation,
  NextOrPreviousPayload,
  ProductMediaState,
  SetCurrentMediaPayload,
} from 'src/components/organisms/ProductMediaCarousel/types';
import { useProductMediaCarousel } from 'src/components/organisms/ProductMediaCarousel/useProductMediaCarousel';
import * as styles from './ProductMediaModal.styles';

const ZoomAndPanWrapper = dynamic(
  async () =>
    (await import(/* webpackChunkName: "ZoomAndPanWrapper" */ './ZoomAndPanWrapper'))
      .ZoomAndPanWrapper,
  { loading: () => <ModalLoader /> },
);

const MEDIA_TRACK_ID = `${MEDIA_ID_PREFIX}${PRODUCT_MEDIA_TABPANEL_CONTAINER_ID}`;

export interface ProductMediaModalStateProps {
  currentMedia: ProductMediaState['currentMedia'];
  isModalOpen: ProductMediaState['isModalOpen'];
  media: MediaItem[];
  navigatedBy: ProductMediaState['navigatedBy'];
}

export interface ProductMediaModalDispatchProps {
  closeModal: VoidFunction;
  onNextMedia: (payload: NextOrPreviousPayload) => void;
  onPrevMedia: (payload: NextOrPreviousPayload) => void;
  setCurrentMedia: (payload: SetCurrentMediaPayload) => void;
}

type ProductMediaModalProps = ProductMediaModalStateProps & ProductMediaModalDispatchProps;

export const ProductMediaModal = ({
  closeModal,
  currentMedia,
  isModalOpen,
  media,
  navigatedBy,
  onNextMedia,
  onPrevMedia,
  setCurrentMedia,
}: ProductMediaModalProps) => {
  const totalMedia = media.length;
  const location: MediaLocation = 'Expanded media';

  /*
    The same scroll/state sync the gallery on the page uses, so swiping works here too and the two
    surfaces cannot disagree about which media is current. Not suspended: while the modal is open it
    is the surface that owns navigation, which is exactly why the carousel underneath is suspended
    (see PDPLayout). Its reconcile window on mount is what lands the modal on the media you tapped.
  */
  const [setMediaTrack, mediaItemRefs] = useProductMediaCarousel(
    (mediaIndex) => setCurrentMedia({ mediaIndex, navigationType: 'swipe', location }),
    currentMedia,
    navigatedBy,
    totalMedia,
  );

  return (
    <Modal closeModal={closeModal} contentLabel="View full screen image" isModalOpen={isModalOpen}>
      <div css={[modalStyles.modalViewport, styles.viewport]}>
        <header css={styles.header}>
          <IconButton aria-label="Close" icon="close" onClick={closeModal} />
        </header>
        <PreventSwipeNavigation css={styles.mediaContainer}>
          <div css={styles.mediaTrack} id={MEDIA_TRACK_ID} ref={setMediaTrack}>
            {media.map((item, i) => (
              <div
                css={styles.tabpanel}
                id={`${MEDIA_ID_PREFIX}${PRODUCT_MEDIA_TABPANEL_ID_PREFIX}${i}`}
                key={mediaKey(item)}
                ref={(node) => {
                  mediaItemRefs.current[i] = node as HTMLElement;
                }}
                role="tabpanel"
              >
                {item.type === 'video' ? (
                  <ProductVideoSlide
                    contentPosition={i + 1}
                    isActive={currentMedia === i}
                    location="Expanded media"
                    playbackId={item.playbackId}
                    posterUrl={item.posterUrl}
                    title={item.title}
                  />
                ) : (
                  <ZoomAndPanWrapper visible={currentMedia === i}>
                    <Image
                      alt={`${item.title}${totalMedia > 1 ? `, ${i + 1} of ${totalMedia}` : ''}`}
                      css={styles.fullScreenMedia}
                      loading="lazy"
                      src={getProductImageURL(item.href, 'original')}
                    />
                  </ZoomAndPanWrapper>
                )}
              </div>
            ))}
          </div>
          {totalMedia > 1 && (
            <>
              <PreviousButton
                controls={MEDIA_TRACK_ID}
                onClick={() => onPrevMedia({ location, totalMedia })}
              />
              <NextButton
                controls={MEDIA_TRACK_ID}
                onClick={() => onNextMedia({ location, totalMedia })}
              />
            </>
          )}
        </PreventSwipeNavigation>
        {totalMedia > 1 && (
          <>
            {/* The dots are a touchscreen addition above the rail; see the styles for why. */}
            <div css={styles.dots}>
              <CarouselDots currentMedia={currentMedia} totalMedia={totalMedia} />
            </div>
            <div css={styles.thumbnails}>
              <div css={styles.imageNumberContainer}>
                <ImageNumberLabel currentImage={currentMedia + 1} totalImages={totalMedia} />
              </div>
              <Thumbnails
                currentMedia={currentMedia}
                location={location}
                media={media}
                onNextMedia={onNextMedia}
                onPrevMedia={onPrevMedia}
                setCurrentMedia={setCurrentMedia}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
